import { motion } from 'framer-motion'
import {
  BookOpen,
  Users,
  Zap,
  CheckCircle2,
  Mail,
  Bot,
} from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

const keyTakeaways = [
  'AI transforms every dev stage',
  'Clear prompts + always review',
  'Balance AI with skill growth',
  'Choose tools that fit your flow',
]

const nextSteps = [
  {
    icon: Zap,
    title: 'Try AI Tools',
    description: 'Start with Copilot or Claude',
    color: 'from-electric-cyan to-blue-500',
  },
  {
    icon: BookOpen,
    title: 'Learn Prompting',
    description: 'Better prompts = better code',
    color: 'from-electric-purple to-purple-600',
  },
  {
    icon: Users,
    title: 'Share & Scale',
    description: 'Team practices & templates',
    color: 'from-aurora-teal to-teal-600',
  },
]

export default function CallToActionSlide({ isActive: _isActive }: SlideProps) {
  return (
    <Slide className="items-center justify-center">
      <StaggerContainer className="w-full max-w-5xl mx-auto text-center">
        {/* Main CTA */}
        <StaggerItem>
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-8"
          >
            <h2 className="font-display text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-4">
              Start Your{' '}
              <span className="text-gradient">AI Journey</span>
            </h2>
            <p className="text-2xl text-white/60 max-w-3xl mx-auto">
              Embrace AI tools to write better code, faster, while maintaining quality and security.
            </p>
          </motion.div>
        </StaggerItem>

        {/* Key Takeaways - inline */}
        <StaggerItem>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-wrap justify-center gap-4 mb-8"
          >
            {keyTakeaways.map((takeaway, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="flex items-center gap-3 glass px-6 py-3 rounded-full"
              >
                <CheckCircle2 className="w-6 h-6 text-electric-cyan flex-shrink-0" />
                <span className="text-lg text-white/70">{takeaway}</span>
              </motion.div>
            ))}
          </motion.div>
        </StaggerItem>

        {/* Next Steps - compact horizontal */}
        <StaggerItem>
          <div className="flex flex-col md:flex-row gap-4 justify-center mb-8">
            {nextSteps.map((step, index) => {
              const Icon = step.icon
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  whileHover={{ scale: 1.03 }}
                  className="glass rounded-2xl p-6 cursor-pointer group flex items-center gap-5 md:flex-1 max-w-sm mx-auto md:mx-0"
                >
                  <div
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-left">
                    <h4 className="font-semibold text-white text-xl">{step.title}</h4>
                    <p className="text-base text-white/50">{step.description}</p>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </StaggerItem>

        {/* Compact Footer */}
        <StaggerItem>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="glass rounded-2xl p-6 max-w-3xl mx-auto mb-[100px]"
          >
            {/* Attribution row */}
            <div className="flex items-center justify-center gap-8 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <Bot className="w-8 h-8 text-electric-cyan" />
                <span className="text-xl text-white/50">+</span>
                <span className="font-semibold text-white text-xl">Alex Usachev</span>
              </div>
              <a
                href="mailto:thothbot@gmail.com"
                className="flex items-center gap-2 text-electric-cyan hover:text-white transition-colors text-lg"
              >
                <Mail className="w-6 h-6" />
                thothbot@gmail.com
              </a>
              <span className="text-white/30 text-lg">December 2025</span>
            </div>

            {/* Funny line */}
            <p className="text-lg text-white/40 italic mb-4">
              "Generated by Claude with human assistance"
              <span className="text-base text-white/30 ml-2">(AI did the work, Alex pressed Enter)</span>
            </p>

            {/* Tech stack inline */}
            <div className="flex justify-center gap-6 text-base text-white/30 pt-4 border-t border-white/10">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-electric-cyan" />
                React + TS
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-electric-purple" />
                Framer Motion
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-electric-magenta" />
                Tailwind
              </span>
            </div>
          </motion.div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
